import {
  CloseCircleOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { Button, Form, Popover, Input } from "antd";
import { cloneElement, isValidElement, useEffect, useState } from "react";
import "./FieldFilter.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

const FieldFilter: React.FC<any> = (props) => {
  const {
    children,
    name,
    label,
    formLabel,
    valueFunction,
    transformValue,
    search = false,
    showButtons = true,
    icon,
  } = props;
  const [value, setValue] = useState<any>();
  const [open, setOpen] = useState<boolean>(false);
  const form = Form.useFormInstance();
  const fieldValue = Form.useWatch(name, form);
  const [searchValue, setSearchValue] = useState("");

  const onClickApply = () => {
    const transformedValue = transformValue
      ? transformValue(fieldValue)
      : fieldValue;
    setValue(transformedValue);
    form.setFieldValue(name, transformedValue);
    form.submit();
    setOpen(false);
  };

  const onClickClear = () => {
    form.resetFields([name]);
    setValue(undefined);
    form.submit();
  };

  const handleOpenChange = (visible: boolean) => {
    setOpen(visible);
    if (!visible) {
      setTimeout(() => {
        const latest = form.getFieldValue(name);
        const transformed = transformValue ? transformValue(latest) : latest;
        const same = JSON.stringify(value) === JSON.stringify(transformed);
        if (!same) {
          setValue(transformed);
          form.setFieldValue(name, transformed);
          form.submit();
        }
      }, 0);
    }
  };

  useEffect(() => {
    if (!fieldValue) {
      setValue(undefined);
    }
  }, [fieldValue]);

  let effectiveChild = children;
  if (isValidElement(children)) {
    const childProps = children.props as any;

    const hasOptions = Array.isArray(childProps.options);

    if (hasOptions) {
      effectiveChild = cloneElement(children, {
        ...childProps,
        getPopupContainer:
          childProps.getPopupContainer ||
          ((node: HTMLElement | null) =>
            (node && node.parentElement) || document.body),
      } as any);
    }

    if (search && searchValue && hasOptions) {
      const filteredOptions = childProps.options.filter(
        (opt: any) =>
          opt.label &&
          opt.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      effectiveChild = cloneElement(
        effectiveChild as any,
        {
          options: filteredOptions,
        } as any
      );
    }
  }

  const displayText = (() => {
    const current = value !== undefined ? value : fieldValue;
    if (current !== undefined && valueFunction) return valueFunction(current);
    return label;
  })();

  return (
    <Popover
      arrow={false}
      placement="bottomLeft"
      trigger={"click"}
      open={open}
      overlayInnerStyle={{ minWidth: 300 }}
      content={() => (
        <div className="field-filter content field-filter-popover">
          {search && (
            <div className="field-filter-search-row">
              <Input
                placeholder={
                  formLabel
                    ? `Search ${formLabel.toLowerCase()}...`
                    : "Search..."
                }
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                allowClear
                className="field-filter-search-input"
                prefix={<FontAwesomeIcon icon={faSearch} />}
              />
            </div>
          )}
          {search && <div className="field-filter-divider" />}
          <div className="field-filter-options-row">
            <Form.Item
              name={name}
              labelAlign="right"
              style={{ marginBottom: 0 }}
            >
              {effectiveChild}
            </Form.Item>
          </div>
          {showButtons && (
            <div className="field-filter-actions-row">
              <button
                type="button"
                className="field-filter-clear-btn"
                onClick={onClickClear}
              >
                Clear All
              </button>
              <Button
                type="primary"
                htmlType="button"
                onClick={onClickApply}
                className="field-filter-apply-btn"
              >
                Apply
              </Button>
            </div>
          )}
        </div>
      )}
      onOpenChange={handleOpenChange}
    >
      <div className={`field-filter`}>
        <div
          className={`container ${open ? "open" : ""} ${
            value ? "has-value" : ""
          }`}
        >
          {icon ? <span className="ff-icon">{icon}</span> : null}
          <span className="ff-text">{displayText}</span>
          <span className="ff-chevron">
            {open ? <UpOutlined /> : <DownOutlined />}
          </span>
        </div>
        {value && (
          <button
            onClick={onClickClear}
            className="button-clear"
            onMouseOver={(evt) => evt.stopPropagation()}
          >
            <CloseCircleOutlined />
          </button>
        )}
      </div>
    </Popover>
  );
};

export default FieldFilter;
